<script lang="ts" setup>
import { useToggle } from "~/core/composables/useToggle";
import LxcError from "~/core/models/LxcError.class";
import {
  NotificationKey,
  showNotificationSuccess,
} from "~/core/utils/notifications";
import type { GroupsUserV2 } from "~/modules/user/models/GroupsUserV2.interface";
import userGroupService from "~/modules/user/services/userGroup.service";
import ILxcLightTrash2 from "~icons/lxc-light/trash-2";

const props = defineProps<{
  userGroup: GroupsUserV2;
  organizationCode: number;
}>();
const emit = defineEmits(["change"]);

const { t } = useI18n();
const [removeModalVisible, toggleRemoveModalVisible] = useToggle();

const showConfirm = computed(() => props.userGroup.nbUsers <= 0); // use "<=" because the user count can be negative (-1 => no count)
const modalMessagesKey = computed(() =>
  showConfirm.value ? "confirmDelete" : "cannotDelete",
);
const title = computed(() => t(`userGroup.${modalMessagesKey.value}.title`));
const description = computed(() =>
  t(`userGroup.${modalMessagesKey.value}.description`, {
    userGroupName: props.userGroup.name,
    usersCount: props.userGroup.nbUsers,
  }),
);

/**
 * When removal confirmation of the user group
 * @param groupName
 */
async function onRemoveUserGroup(groupName: string) {
  const removeResponse = await userGroupService.removeOrganizationUserGroup(
    props.organizationCode,
    groupName,
  );

  if (LxcError.check(removeResponse)) {
    removeResponse.notify(NotificationKey.removeError);
  } else {
    showNotificationSuccess(t(NotificationKey.removeSuccess));
    toggleRemoveModalVisible();
    emit("change");
  }
}

const onDeleteGroupClick = () => {
  toggleRemoveModalVisible(true);
};
</script>

<template>
  <lxc-button
    html-type="button"
    type="borderless"
    :icon="ILxcLightTrash2"
    :title="t('userGroup.button.delete')"
    class="invisible"
    @click.stop.prevent="onDeleteGroupClick"
  >
  </lxc-button>

  <confirm-modal
    :show-confirm="showConfirm"
    :is-dialog-visible="removeModalVisible"
    :title="title"
    :description="description"
    :icon="ILxcLightTrash2"
    icon-color-theme="error"
    color-theme="danger"
    @cancel="toggleRemoveModalVisible"
    @confirm="onRemoveUserGroup(userGroup?.name)"
  />
</template>
<style lang="scss" scoped>
button {
  :deep(svg) {
    height: 20px;
    width: 20px;
  }
}
</style>
